/* Vokal & Şan — ders içerikleri. Blok türleri ve satır içi işaretler: ./blocks.ts */
import { compare, go, h, type Lesson, list, p, play, pn, steps, table, tip } from './blocks';

export const vokal: Lesson[] = [
  {
    id: 'v1',
    title: 'Güvenli ve düzenli çalışma',
    minutes: 4,
    summary: `Sesini zorlamadan, düzenli ve güvenli şekilde çalıştırmanın temel kurallarını öğren.`,
    blocks: [
      p(
        `Ses tellerin, gırtlağının içinde saniyede yüzlerce kez titreşen küçük ve hassas dokulardır. Onları çalıştırmak bir sporcunun antrenmanına benzer: düzenli, kademeli ve ısınarak.`,
      ),
      h(`Temel kurallar`),
      list([
        `**Her gün kısa çalış.** Günde 15–20 dakika, haftada bir yapılan uzun bir seanstan daha faydalıdır.`,
        `**Her zaman ısın.** Zor egzersizlere ya da yüksek notalara ısınmadan geçme.`,
        `**Zorlama.** Ulaşamadığın bir notaya bağırarak ya da boğazını sıkarak ulaşmaya çalışma; aralığın çalıştıkça genişler.`,
        `**Ağrı bir dur işaretidir.** Boğazında yanma, ağrı ya da batma hissedersen o gün egzersizi bırak.`,
        `**Su iç.** Gün boyunca düzenli su içmek ses tellerinin nemli kalmasına yardım eder.`,
      ]),
      tip(
        'warn',
        'Ne zaman doktora görünmeli?',
        `Ses kısıklığın 2–3 haftadan uzun sürerse, konuşurken ya da şarkı söylerken ağrı hissediyorsan veya sesinde ani bir değişiklik olduysa bir **KBB (kulak burun boğaz) uzmanına** görün. Bu uygulama ses eğitimine destek olur, ama bir öğretmenin ya da doktorun yerini tutmaz.`,
      ),
      h(`Bu bölümde nasıl çalışacaksın?`),
      steps([
        ['Ses aralığını belirle', `Önce ses aralığı testini yap. Egzersizler senin aralığına göre ayarlanır.`],
        ['Isın', `Her seansa kısa bir ısınma rutiniyle başla: nefes, dudak titreşimi, yumuşak gamlar.`],
        ['Odaklan', `Bir ya da iki egzersiz seç ve onlara odaklan. Mikrofon, söylediğin notayı anlık olarak gösterir.`],
        ['Soğu', `Seansı yumuşak, inen gamlarla ve hafif mırıldanmayla bitir.`],
      ]),
      tip(
        'info',
        'Kulaklık önerisi',
        `Kulaklık kullanırsan uygulamanın çaldığı referans notalar mikrofona karışmaz ve algılama daha doğru olur.`,
      ),
      go('Ses aralığı testini yap', 'vokal.aralik-testi'),
      go('Isınma rutini', 'vokal.isinma'),
    ],
  },
  {
    id: 'v2',
    title: 'Nefes: diyafram nefesi',
    minutes: 6,
    summary: `Şarkı söylemenin motoru nefestir; omuzlarını kaldırmadan, karın ve bel çevresiyle nefes almayı öğren.`,
    blocks: [
      p(
        `Günlük hayatta çoğumuz göğsümüzün üst kısmıyla, omuzlarımızı hafifçe kaldırarak nefes alırız. Şarkı söylerken ise daha derin ve kontrollü bir nefese ihtiyaç vardır. Buna genellikle **diyafram nefesi** ya da karın nefesi denir.`,
      ),
      list([
        `Nefes alırken **omuzların kalkmaz**, göğsün sakin kalır.`,
        `**Karnın ve belinin çevresi** öne ve yanlara doğru genişler.`,
        `Nefes verirken bu genişliği bir anda bırakma; yavaşça ve kontrollü olarak geri gelsin.`,
      ]),
      tip(
        'info',
        'Kendini kontrol et',
        `Bir elini göğsüne, diğerini göbeğinin hemen üstüne koy. Nefes alırken alttaki el hareket etmeli, üstteki neredeyse sabit kalmalı. İlk denemelerde sırtüstü yatmak işi kolaylaştırır.`,
      ),
      h(`Rehberli nefes: 4 – 4 – 8`),
      p(
        `4 saniye burnundan nefes al, 4 saniye tut, 8 saniyede "sss" sesiyle yavaşça ver. Rahatladıkça veriş süresini uzatabilirsin.`,
      ),
      { t: 'breath', pattern: [4, 4, 8], rounds: 3 },
      h(`"Sss" egzersizi`),
      p(
        `Dişlerinin arasından ince ve **eşit** bir "sss" sesi çıkar. Amaç sesi olabildiğince sabit tutmak: ne patlayarak başlasın ne de sonunda sönsün. Mikrofon, "sss" sesini ne kadar süre koruyabildiğini ölçer.`,
      ),
      tip(
        'success',
        'Hedef',
        `İlk başta 15–20 saniye iyi bir başlangıçtır ve süre düzenli çalışmayla uzar. Önemli olan uzun tutmaktan çok eşit tutmaktır.`,
      ),
      tip('warn', 'Başın dönerse', `Nefes egzersizleri sırasında başın dönerse ara ver ve normal nefesine dön.`),
      go('"Sss" süresini ölç', 'vokal.egz.nefes.2'),
      go('Sabit ses egzersizi', 'vokal.egz.nefes.1'),
      go('Diyafram nefes verirken ne yapar?', 'az-bilinenler.diyafram'),
    ],
  },
  {
    id: 'v3',
    title: 'Duruş ve gevşeme',
    minutes: 5,
    summary: `Rahat ve dengeli bir duruş sesin özgürce çıkmasını sağlar; vücuttaki gerginliği bırakmayı öğren.`,
    blocks: [
      p(
        `Ses bütün vücudunla üretilir. Omuzlarda, çenede ya da boyunda biriken gerginlik doğrudan sesine yansır: ses sıkışır, incelir ya da çabuk yorulur.`,
      ),
      h(`Şarkı söyleme duruşu`),
      list([
        `**Ayaklar** omuz genişliğinde, ağırlık iki ayağa eşit dağılmış.`,
        `**Dizler** kilitli değil, hafif esnek.`,
        `**Omurga** uzun; başının tepesinden yukarı doğru hafifçe çekiliyormuş gibi düşün.`,
        `**Omuzlar** aşağıda ve geride, ama zorlanmadan.`,
        `**Çene** serbest; dişlerini sıkma.`,
        `**Dil** gevşek, ucu alt ön dişlerinin arkasına hafifçe değiyor.`,
      ]),
      h(`Gevşeme hareketleri`),
      steps([
        [
          'Boyun ve omuzlar',
          `Omuzlarını kulaklarına doğru kaldır, iki saniye tut ve bırak. Üç kez tekrarla. Sonra başını yavaşça sağa ve sola eğ.`,
        ],
        [
          'Esneme ve iç çekme',
          `Gerçek bir esnemenin başındaki açıklığı hisset. Sonra yüksekten aşağıya doğru rahat bir "haaa" ile iç çek. Bu hareket gırtlağı ve yumuşak damağı gevşetir.`,
        ],
        [
          'Çene masajı',
          `Parmak uçlarınla elmacık kemiklerinin altındaki çene kaslarına küçük daireler çizerek masaj yap. Ağzın hafif açık kalsın.`,
        ],
        [
          'Dudak titreşimi',
          `Dudaklarını gevşek tutup aralarından hava üfleyerek "brrr" sesi çıkar. Sonra buna ses ekle ve yukarı aşağı kaydır.`,
        ],
      ]),
      play('Dudak titreşimiyle izleyebileceğin kısa kalıp', pn({ seq: 'C4 E4 G4 E4 C4:2', bpm: 80 })),
      tip(
        'info',
        'Dudakların titremiyorsa',
        `İşaret parmaklarınla yanaklarını ağzının köşelerinden hafifçe yukarı it. Bu, dudakların gevşemesine yardım eder.`,
      ),
      tip(
        'success',
        'Her seansın başında',
        `Bu hareketler iki üç dakika sürer ve ısınmanın ilk adımıdır. Isınma rutininde de aynı sırayla yer alırlar.`,
      ),
      go('Isınma rutinine geç', 'vokal.isinma'),
      go('Pipetle şarkı söylemek neden işe yarar?', 'az-bilinenler.pipet'),
    ],
  },
  {
    id: 'v4',
    title: 'Ses aralığın ve ses tipin',
    minutes: 5,
    summary: `Rahatça söyleyebildiğin en kalın ve en ince notalar ses aralığını belirler; ses tipleri ise bu aralığın ve tınının bir sınıflandırmasıdır.`,
    blocks: [
      p(
        `**Ses aralığı**, rahatça ve sağlıklı şekilde söyleyebildiğin en kalın ve en ince notalar arasındaki mesafedir. Zorlayarak bir anlığına ulaşabildiğin notalar bu aralığa dahil değildir.`,
      ),
      h(`Ses tipleri`),
      table(
        ['Ses tipi', 'Yaklaşık aralık', 'Genelde'],
        [
          ['Bas', '{{n:E2}} – {{n:E4}}', 'En kalın erkek sesi'],
          ['Bariton', '{{n:A2}} – {{n:A4}}', 'En yaygın erkek sesi'],
          ['Tenor', '{{n:C3}} – {{n:C5}}', 'En ince erkek sesi'],
          ['Alto', '{{n:F3}} – {{n:F5}}', 'En kalın kadın sesi'],
          ['Mezzo-soprano', '{{n:A3}} – {{n:A5}}', 'Orta kadın sesi'],
          ['Soprano', '{{n:C4}} – {{n:C6}}', 'En ince kadın sesi'],
        ],
      ),
      tip(
        'info',
        'Aralık tek ölçüt değil',
        `Ses tipi yalnızca aralıkla belirlenmez; sesinin **tınısı** (rengi), en rahat söylediğin bölge ve **geçiş bölgelerinin** yeri de önemlidir. Kesin bir sınıflandırma için bir şan öğretmeniyle çalışmak en doğrusudur.`,
      ),
      compare([
        ['Bas ve bariton bölgesi', pn({ seq: 'A2 C3 E3' })],
        ['Tenor ve alto bölgesi', pn({ seq: 'C4 E4 G4' })],
        ['Soprano bölgesi', pn({ seq: 'C5 E5 G5' })],
      ]),
      h(`Aralık testi nasıl yapılır?`),
      steps([
        ['Isın', `Teste ısınmadan başlama; birkaç dakika dudak titreşimi ve yumuşak gamlar yeterli.`],
        [
          'En kalın notan',
          `Rahat bir "aa" ile başla ve yavaşça aşağı kay. Sesin hırıltıya dönmeden önce rahatça tutabildiğin en kalın notada dur ve bir saniye tut.`,
        ],
        [
          'En ince notan',
          `Aynı şekilde yukarı kay. Boğazını sıkmadan ve bağırmadan tutabildiğin en ince notada dur. Bu sırada kafa sesine geçebilirsin.`,
        ],
        [
          'Sonucu oku',
          `Uygulama iki notayı ve aradaki mesafeyi gösterir, bir ses tipi önerir ve egzersizleri bu aralığa göre ayarlar.`,
        ],
      ]),
      tip(
        'success',
        'Aralığın değişir',
        `Sabahları, yorgunken ya da hastayken aralığın daralabilir; düzenli çalışmayla da genişler. Testi birkaç haftada bir tekrarlamak iyi bir fikirdir.`,
      ),
      go('Ses aralığı testini başlat', 'vokal.aralik-testi'),
    ],
  },
  {
    id: 'v5',
    title: 'Perde doğruluğu',
    minutes: 5,
    summary: `Duyduğun notayı doğru yükseklikte söylemek bir alışkanlıktır; dinleme ve düzeltme döngüsünü öğren.`,
    blocks: [
      p(
        `Detone söylemek, yani notanın biraz altında ya da üstünde kalmak, çoğu zaman bir kulak sorunu değil **dikkat ve alışkanlık** sorunudur. Notayı söylemeden önce zihninde duymayı öğrendiğinde doğruluğun hızla artar.`,
      ),
      steps([
        ['Dinle', `Referans notayı dikkatle dinle. Henüz söyleme.`],
        ['İçinden duy', `Notayı zihninde tekrar et, sanki hâlâ çalıyormuş gibi.`],
        [
          'Mırıldan',
          `Ağzın kapalıyken hafif bir "mmm" ile notayı dene. Mırıldanmak daha az çaba ister ve düzeltmesi kolaydır.`,
        ],
        [
          'Söyle ve tut',
          `Şimdi bir ünlüyle ("aa" ya da "uu") söyle ve sabit tut. Ekrandaki göstergeye bak: notanın altında mısın, üstünde mi?`,
        ],
      ]),
      play('Örnek referans notası', pn({ seq: 'A3:3' })),
      h(`Sent nedir?`),
      p(
        `Yarım ses 100 eşit parçaya bölünür ve her birine **sent** denir. Göstergede 0 sent tam isabettir. Eksi değerler notanın altında (pes), artı değerler üstünde (tiz) olduğunu gösterir.`,
      ),
      list([
        `**±10 sent içinde:** çok iyi; çoğu dinleyici farkı duymaz.`,
        `**±20 sent içinde:** iyi; bu bölgede tutabiliyorsan doğru yoldasın.`,
        `**±50 sent ve üstü:** komşu notaya yaklaştın; referansı dikkatle tekrar dinle.`,
      ]),
      compare([
        ['Referans', pn({ seq: 'A3:2' })],
        ['Yarım ses pes (−100 sent)', pn({ seq: 'G#3:2' })],
        ['Yarım ses tiz (+100 sent)', pn({ seq: 'A#3:2' })],
      ]),
      tip(
        'info',
        'En sık hata',
        `Uzun notalarda sesin sonuna doğru düşmesi çok yaygındır. Notayı tutarken nefes desteğini bırakma; notanın sonunu da başı kadar önemse.`,
      ),
      tip(
        'success',
        'Kendi sesini duy',
        `Bir elini kulağının arkasına hafifçe çanak gibi koymak, kendi sesini daha net duymanı sağlar. Koro şarkıcılarının sık kullandığı bir yöntemdir.`,
      ),
      go('Notayı Tut: Kolay (±50 sent)', 'vokal.egz.tut.1'),
      go('Notayı Tut: Orta (±30 sent)', 'vokal.egz.tut.2'),
      go('Kayıtta sesin neden yabancı gelir?', 'az-bilinenler.kayitta-sesin'),
    ],
  },
  {
    id: 'v6',
    title: 'Gamlar ve arpejler',
    minutes: 6,
    summary: `Ses eğitiminin temel taşları olan 5'li gam, oktav gam, arpej ve 3'lüler kalıplarını tanı.`,
    blocks: [
      p(
        `Gamlar ve arpejler; sesin notadan notaya temiz geçmesini, perde doğruluğunu ve aralığının her bölgesini çalıştırır. Şan derslerinde her gün tekrarlanmalarının nedeni budur.`,
      ),
      h(`5'li gam`),
      p(`Gamın ilk beş notasını çıkıp geri inersin: 1-2-3-4-5-4-3-2-1. En rahat ve en yaygın ısınma kalıbıdır.`),
      play("5'li gam", pn({ seq: 'C4 D4 E4 F4 G4 F4 E4 D4 C4:2', bpm: 100 })),
      h(`Oktav gam`),
      play('Bir oktav yukarı ve geri', pn({ seq: 'C4 D4 E4 F4 G4 A4 B4 C5 B4 A4 G4 F4 E4 D4 C4:2', bpm: 110 })),
      h(`Arpej: 1-3-5-8`),
      p(
        `Arpejde akorun notalarını tek tek söylersin: 1-3-5-8-5-3-1. Adımlar yerine atlamalar olduğu için perde doğruluğunu daha çok zorlar.`,
      ),
      play('Arpej', pn({ seq: 'C4 E4 G4 C5 G4 E4 C4:2', bpm: 90 })),
      h(`3'lüler`),
      play("3'lüler kalıbı: 1-3, 2-4, 3-5, 4-6, 5-3-1", pn({ seq: 'C4 E4 D4 F4 E4 G4 F4 A4 G4 E4 C4:2', bpm: 100 })),
      h(`Hecelerle çalış: ma-me-mi-mo-mu`),
      p(
        `Aynı kalıbı her notada farklı bir heceyle söyle: ma, me, mi, mo, mu. "M" sesi sesi öne taşır. Ünlü değiştikçe ağzın ve dilin şekli değişir, ama perde sabit kalmalıdır.`,
      ),
      tip(
        'info',
        'Senin aralığına göre',
        `Buradaki örnekler {{n:C4}} çevresinde çalıyor. Erkek sesleri bunları genelde bir oktav aşağıda söyler. Egzersiz ekranında kalıplar ses aralığına göre ayarlanır ve her tekrarda yarım ses yukarı taşınır.`,
      ),
      go("Gam Merdiveni: 5'li gam", 'vokal.egz.gam.1'),
      go('Arpej egzersizi', 'vokal.egz.gam.3'),
      go('Nağme nasıl gelişir?', 'az-bilinenler.nagme'),
    ],
  },
  {
    id: 'v7',
    title: 'Rezonans ve ünlüler',
    minutes: 5,
    summary: `Sesinin nerede yankılandığını hissetmeyi ve ünlüleri perdeyi bozmadan şekillendirmeyi öğren.`,
    blocks: [
      p(
        `Ses tellerinde üretilen ses; boğaz, ağız ve burun boşluklarında güçlenir ve renk kazanır. Buna **rezonans** denir. İyi bir rezonansla daha az çaba harcayarak daha dolgun ve uzağa taşınan bir ses elde edersin.`,
      ),
      h(`"Mmm" ve "ng" ile ileri yerleşim`),
      steps([
        [
          'Mırıldan',
          `Dudakların kapalı, dişlerin hafif aralık, rahat bir "mmm" söyle. Dudaklarında ve burnunun çevresinde hafif bir karıncalanma hissetmelisin.`,
        ],
        [
          '"Ng" sesine geç',
          `"Renk" kelimesini söylerken "k" sesine geçmeden önceki genizden gelen "n" sesini uzat. Dilinin arkası yumuşak damağa değer ve ses burundan çıkar.`,
        ],
        [
          'Ünlüye aç',
          `"Mmm" ya da "ng" sesini koruyarak yavaşça "aa" ünlüsüne aç. Öndeki titreşim hissini kaybetmemeye çalış.`,
        ],
      ]),
      play('Mırıldanmak için örnek kalıp', pn({ seq: 'G3 A3 B3 C4 D4 C4 B3 A3 G3:2', bpm: 90 })),
      h(`Açık ve kapalı ünlüler`),
      list([
        `**Açık ünlüler** (a, e, o): ağız daha açık, ses daha parlak ve güçlü.`,
        `**Kapalı ünlüler** (ı, i, u, ü): ağız daha kapalı, ses daha yumuşak ve odaklı.`,
        `Yüksek notalarda ünlüleri biraz "yuvarlamak", örneğin "i" sesini "ü" sesine yaklaştırmak, çoğu zaman sesi rahatlatır.`,
      ]),
      tip(
        'info',
        'Çene ve ağız',
        `Yüksek notalarda ağzını yanlara yaymak yerine dikey olarak açmayı dene, sanki bir elmayı ısıracakmış gibi. Çeneni zorla aşağı itme; serbestçe düşmesine izin ver.`,
      ),
      tip(
        'success',
        'Perde sabit kalsın',
        `Ünlü değiştirirken perde sık sık kayar. Notayı Tut egzersizinde aynı notayı önce "uu", sonra "aa", sonra "ii" ile söyle ve göstergenin yerinden oynamadığını kontrol et.`,
      ),
      go('Notayı Tut', 'vokal.egz.tut.1'),
      go('Melodi Yankısı', 'vokal.egz.yanki.1'),
    ],
  },
  {
    id: 'v8',
    title: 'Registerler ve geçiş bölgesi',
    minutes: 6,
    summary: `Sesinin farklı bölgelerini, aralarındaki geçişi ve bu geçişi sirenlerle yumuşatmayı öğren.`,
    blocks: [
      p(
        `Kalından inceye doğru kaydığında sesinin karakterinin bir noktada değiştiğini fark edersin. Bu farklı karakterdeki bölgelere **register** denir.`,
      ),
      list([
        `**Göğüs sesi:** Konuşma sesine yakın, dolgun ve güçlü. Titreşim göğüste hissedilir.`,
        `**Kafa sesi:** Daha hafif ve ince. Titreşim daha çok yüzde ve başta hissedilir.`,
        `**Karışık ses (mix):** İkisinin dengeli bir birleşimi; geçiş bölgesinde sesin kopmadan ilerlemesini sağlar.`,
      ]),
      h(`Geçiş bölgesi`),
      p(
        `Registerler arasındaki geçiş noktasına **geçiş bölgesi** (passaggio) denir. Burada ses kırılabilir ya da çatallanabilir. Bu tamamen normaldir ve çalışmayla yumuşar.`,
      ),
      table(
        ['Ses grubu', 'Geçişlerin yaklaşık bölgesi'],
        [
          ['Erkek sesleri', '{{n:D4}} – {{n:G4}} civarı'],
          ['Kadın sesleri', '{{n:E4}} – {{n:F4}} civarı ve {{n:C5}} – {{n:F5}} civarı'],
        ],
      ),
      tip(
        'info',
        'Kişiden kişiye değişir',
        `Bu bölgeler yaklaşıktır; ses tipine ve kişiye göre değişir. Kendi geçişini bulmak için yavaşça yukarı kay ve sesinin karakterinin değiştiği yeri fark et.`,
      ),
      h(`Sirenlerle geçişi yumuşat`),
      p(
        `Siren, sesini hiç kesmeden kalından inceye ve geri kaydırmaktır. Dudak titreşimiyle, "ng" sesiyle ya da "uu" ünlüsüyle yapılabilir. Amaç, geçiş bölgesinden ses kopmadan geçmektir.`,
      ),
      steps([
        ['Küçük başla', `Önce rahat bölgende beş notalık küçük kaydırmalar yap.`],
        [
          'Geçişe yaklaş',
          `Kaydırmayı yavaş yavaş genişlet ve geçiş bölgesinden geçir. Ses hafifçe incelirse izin ver; zorla göğüs sesinde tutmaya çalışma.`,
        ],
        [
          'Hafif kal',
          `Sesi yüksek değil, hafif ve rahat tut. Sirenler bir güç egzersizi değil, esneklik egzersizidir.`,
        ],
      ]),
      play('Siren için yol gösteren kalıp', pn({ seq: 'C4 E4 G4 C5 G4 E4 C4:2', bpm: 120 })),
      go('Siren Takibi', 'vokal.egz.siren.1'),
    ],
  },
  {
    id: 'v9',
    title: 'Vibrato',
    minutes: 5,
    summary: `Vibratonun ne olduğunu, neden zorlanmaması gerektiğini ve nasıl kendiliğinden ortaya çıktığını öğren.`,
    blocks: [
      p(
        `**Vibrato**, uzun bir notada perdenin hafif ve düzenli şekilde aşağı yukarı dalgalanmasıdır. Sese sıcaklık ve canlılık katar.`,
      ),
      list([
        `Doğal vibrato saniyede yaklaşık **5–7 salınım** yapar.`,
        `Perde, notanın çevresinde genellikle en fazla yarım ses kadar iner çıkar. Pop müzikte bu dalgalanma daha dar, klasik şanda daha geniştir.`,
        `Vibrato bir süs değil, **gevşek ve iyi desteklenmiş** bir sesin doğal sonucudur.`,
      ]),
      tip(
        'warn',
        'Zorlama',
        `Vibratoyu çeneni, karnını ya da gırtlağını sallayarak üretmeye çalışma. Bu, sesi titrek ve gergin yapar. Doğru vibrato zorlanmaz; ortaya çıkmasına izin verilir.`,
      ),
      h(`Vibratoyu bulmak`),
      steps([
        ['Düz tut', `Rahat bölgende bir notayı "aa" ile, vibratosuz ve tamamen düz tut.`],
        [
          'Bırak',
          `Notayı tutarken nefes desteğini koru ama boğazındaki kontrolü gevşet. Ses kendiliğinden hafifçe dalgalanmaya başlayabilir.`,
        ],
        [
          'İkisi arasında geç',
          `Aynı notada önce düz, sonra serbest söyle. İkisini istediğin zaman seçebilmek asıl hedeftir.`,
        ],
      ]),
      play('Tutmak için referans nota', pn({ seq: 'G3:4' })),
      tip(
        'info',
        'Ekranda gör',
        `Canlı ses analizinde perde çizgin, vibrato yaptığında düzgün dalgalar çizer. Dalgaların düzenli ve eşit aralıklı olması iyi bir işarettir.`,
      ),
      tip(
        'success',
        'Sabır',
        `Vibrato çoğu kişide aylar içinde, nefes ve gevşeme oturdukça ortaya çıkar. Çıkmıyorsa düz ve temiz söylemeye devam etmek de değerli bir beceridir.`,
      ),
      go('Canlı ses analizini aç', 'araclar.analiz'),
      go('Sabit ses egzersizi', 'vokal.egz.nefes.1'),
      go('Vibrato, tril ve tremolo', 'az-bilinenler.vibrato-tril'),
    ],
  },
  {
    id: 'v10',
    title: 'Ses sağlığı ve soğuma',
    minutes: 5,
    summary: `Sesini uzun yıllar sağlıklı tutacak günlük alışkanlıkları ve seans sonu soğuma egzersizini öğren.`,
    blocks: [
      p(`Ses eğitimi yalnızca egzersiz saatlerinde değil, gün boyunca sesini nasıl kullandığınla da ilgilidir.`),
      h(`Günlük alışkanlıklar`),
      list([
        `**Su:** Gün boyu düzenli su iç. Kuru havada, özellikle kışın kaloriferli odalarda, hava nemlendirici yardımcı olabilir.`,
        `**Bağırmaktan kaçın:** Gürültülü ortamlarda sesini yükseltmek yerine karşındakine yaklaşarak konuş.`,
        `**Fısıldamayı abartma:** Ses kısıkken uzun süre fısıldamak da ses tellerini zorlayabilir; mümkünse sesini dinlendir.`,
        `**Boğaz temizleme yerine yutkun:** Sürekli öksürerek ya da hırıldayarak boğazını temizlemek ses tellerini tahriş eder. Yutkunmak ya da bir yudum su içmek daha nazik bir yoldur.`,
        `**Uyku:** Yorgunluk sese hemen yansır. Önemli bir performanstan önce iyi uyu.`,
        `**Reflü:** Mide asidi gırtlağı tahriş edebilir. Sabahları ses kısıklığı ya da boğazda sürekli yanma hissi varsa bunu bir doktora danış.`,
        `**Sigara ve alkol:** İkisi de ses tellerini kurutur ve tahriş eder.`,
      ]),
      h(`Soğuma`),
      p(
        `Yoğun bir seanstan sonra sesini hemen sessizliğe bırakmak yerine birkaç dakikalık bir soğuma yap. Bu, sporcuların antrenman sonrası esnemesine benzer.`,
      ),
      steps([
        ['Dudak titreşimi', `Rahat bölgende, yukarıdan aşağıya doğru hafif dudak titreşimleri yap.`],
        ['İnen gamlar', `"Uu" ünlüsüyle yumuşak, inen 5'li gamlar söyle.`],
        [
          'Mırıldanma',
          `Konuşma sesinin biraz üstünden başlayan rahat bir "mmm" ile, iç çeker gibi birkaç kez aşağı kay.`,
        ],
      ]),
      play("İnen 5'li gam (kendi aralığında söyle)", pn({ seq: 'G4 F4 E4 D4 C4:2', bpm: 80 })),
      tip(
        'warn',
        'Uyarı işaretleri',
        `Şu durumlarda bir **KBB uzmanına** görün: 2–3 haftadan uzun süren ses kısıklığı, konuşurken ya da yutkunurken ağrı, sesin tamamen kaybolması, kan tükürme ya da nefes almakta zorlanma.`,
      ),
      tip(
        'success',
        'Dinlenmek de çalışmaktır',
        `Sesin yorgun hissettiğinde bir gün ara vermek ilerlemeni yavaşlatmaz; ses tellerinin toparlanmasına fırsat verir.`,
      ),
      go('Isınma rutini', 'vokal.isinma'),
      go('İçtiğin su ses tellerine değer mi?', 'az-bilinenler.ses-telleri'),
    ],
  },
];
